import type { HandbookTreeNode } from '@dreamai/types';

export interface FlatHandbookNode {
  slug: string;
  title: string;
  depth: number;
}

/** Depth-first flattening of the tree in display order. */
export function flattenTree(
  nodes: HandbookTreeNode[],
  depth = 0,
): FlatHandbookNode[] {
  return nodes.flatMap((node) => [
    { slug: node.slug, title: node.title, depth },
    ...flattenTree(node.children, depth + 1),
  ]);
}

export function findNode(
  nodes: HandbookTreeNode[],
  slug: string,
): HandbookTreeNode | null {
  for (const node of nodes) {
    if (node.slug === slug) return node;
    const child = findNode(node.children, slug);
    if (child) return child;
  }
  return null;
}

/** Slugs of every page with a pending draft, in display order. */
export function draftSlugs(nodes: HandbookTreeNode[]): string[] {
  return nodes.flatMap((node) => [
    ...(node.hasDraft ? [node.slug] : []),
    ...draftSlugs(node.children),
  ]);
}

/**
 * Pages that may become `slug`'s parent: every page except itself and its
 * descendants (the backend rejects those too, this just keeps them out of
 * the picker).
 */
export function parentCandidates(
  nodes: HandbookTreeNode[],
  slug: string | null,
): FlatHandbookNode[] {
  if (!slug) return flattenTree(nodes);
  const self = findNode(nodes, slug);
  const excluded = new Set(
    self ? flattenTree([self]).map((n) => n.slug) : [slug],
  );
  return flattenTree(nodes).filter((n) => !excluded.has(n.slug));
}

export function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/** Machine-readable reason, whether the backend sent it as `code` or `error`. */
export function handbookErrorCode(err: unknown): string | undefined {
  const data = (
    err as { response?: { data?: { code?: string; error?: string } } }
  )?.response?.data;
  return data?.code ?? data?.error;
}

const CODE_MESSAGES: Record<string, string> = {
  handbook_page_exists: 'A page with this slug already exists.',
  handbook_page_not_found: 'That page no longer exists.',
  handbook_no_draft: 'This page has no draft to work on.',
  handbook_invalid_parent: 'That page cannot be the parent of this one.',
  handbook_snapshot_not_found: 'That point in history no longer exists.',
  handbook_import_not_found: 'That import no longer exists.',
  handbook_import_not_ready:
    'This import is still working or has already been dealt with, so it cannot be published yet.',
  handbook_import_no_drafts:
    'None of this import’s drafts are left to publish.',
  handbook_import_not_discardable: 'This import cannot be discarded any more.',
  handbook_notion_not_connected:
    'Notion is not connected for this organisation. Connect it under the Connected apps tab, then start the import again.',
  handbook_google_not_connected:
    'That Google account is no longer linked to your login. Link it under Settings → Connected accounts, then pick the files again.',
  handbook_encryption_not_configured:
    'Handbook imports are not available on this deployment: the handbook encryption key is not configured.',
};

export function handbookErrorMessage(err: unknown, fallback: string): string {
  const code = handbookErrorCode(err);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  const data = (err as { response?: { data?: { error?: string } } })?.response
    ?.data;
  return data?.error || fallback;
}
